// client/src/pages/board/EditResponse.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';

import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import { getResponseById, updateResponse, uploadResponseFiles } from '../../api/boardApi';
import { uploadEditorImage } from '../../api/common';

import FileAttachment from '../../components/FileAttachment/FileAttachment';

function EditResponse() {
  const { id } = useParams(); // 응답 id
  const [searchParams] = useSearchParams();
  const codeId = searchParams.get('code_id');

  const currentPage = searchParams.get('page') || 1;
  
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [parentId, setParentId] = useState(null); // 원문 id 저장

  const pendingImages = useRef(new Map());

  const storeImageLocally = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    pendingImages.current.set(blobUrl, file);
    return blobUrl;
  }, []);

  const editor = useEditorWithDragDrop(tiptapExtensions, '', { onImageUpload: storeImageLocally });

  // 응답 불러오기
  useEffect(() => {
    if (!editor) return;

    getResponseById(id, token)
      .then((res) => {
        //console.log("응답 JSON:", res);
        setTitle(res.title);
        setParentId(res.parent_id);
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(res.content || '');
        }
        setLoading(false);

        // ✅ Sequelize dataValues 반영
        if (Array.isArray(res.files)) {
          const existingFiles = res.files.map(f => {
            const file = f.dataValues || f;
            return {
              id: file.id,
              name: file.originalName,
              size: file.size,
              type: file.mimeType,
              path: file.path,
              isNew: false,
              isDeleted: false,
            };
          });
          setFiles(existingFiles);
        }
      })
      .catch(() => {
        alert('응답을 불러오지 못했습니다.');
        navigate('/board');
      });
  }, [id, navigate, token, editor]);

  // 수정 처리
  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      // 본문 이미지 업로드 처리 (blob URL → server URL)
      let content = editor.getHTML();
      for (const [blobUrl, file] of pendingImages.current) {
        if (content.includes(blobUrl)) {
          const serverUrl = await uploadEditorImage(file, token, 'board', id);
          content = content.replaceAll(blobUrl, serverUrl);
        }
        URL.revokeObjectURL(blobUrl);
      }
      pendingImages.current.clear();

      // ✅ 삭제할 파일 id 배열 그대로 전달
      const deletedFileIds = files.filter(f => f.isDeleted && f.id != null).map(f => f.id);

      await updateResponse(
        id,
        {
          title,
          content,
          deletedFileIds, // 문자열화하지 않고 배열 그대로
        },
        token
      );

      // 새로 첨부한 파일 업로드
      const newFiles = files.filter(f => f.isNew);
      if (newFiles.length > 0) {
        const formData = new FormData();
        formData.append('code_id', codeId);
        newFiles.forEach(f => formData.append('files', f.file));
        await uploadResponseFiles(id, formData, token);
      }

      // 수정 후 원문 상세로 이동
      //navigate(`/post/${parentId}?code_id=${codeId}`);
      navigate(`/board?page=${currentPage}&code_id=${codeId}`);
    } catch (err) {
      console.error('응답 수정 실패:', err);
      alert('응답 수정 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid view-response-edit">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleUpdate}>
            {/* 제목 */}
            <div className="mb-3">
              <label className="form-label fw-bold">제목</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* 내용 */}
            <div className="mb-3">
              <label className="form-label fw-bold">내용</label>
              <div className="border p-2 rounded">
                {editor && <EditorToolbar editor={editor} onImageUpload={storeImageLocally} />}
                <EditorContent editor={editor} className="editor-content" />
              </div>
            </div>

            {/* 첨부파일 */}
            <div className="mb-3">
              <label className="form-label fw-bold">첨부파일</label>
              <FileAttachment files={files} setFiles={setFiles} mode="edit" />
            </div>

            {/* 버튼 영역 */}
            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-success">
                수정 완료
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(`/post/${parentId}?code_id=${codeId}`)}
              >
                원문으로
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditResponse;
